import { throttle } from 'lodash-es';
import dayjs from 'dayjs';
import BaseComponent from '../BaseComponent';
import SelectorEngine from '../../utils/dom/selector-engine';
import ValidationUtils from '../../utils/validation';
import DefaultSubmit from 'formvalidation/plugins/DefaultSubmit';
import Message from 'formvalidation/plugins/Message';
import SubmitButton from 'formvalidation/plugins/SubmitButton';
import Trigger from 'formvalidation/plugins/Trigger';
import formValidation from '../../../vendors/FormValidation/core/Core';
import Modal from '../../components/Modal';
import Manipulator from '../../utils/dom/manipulator';
import EventHandler from '../../utils/dom/event-handler';

const THROTTLE_INTERVAL_MS = 100;
const MAX_SCROLL_HEIGHT = 600;
const BOTTOM_BORDER_HEIGHT = 1;

const DATE_FORMAT_YYYY_MM_DD = 'YYYY-MM-DD';

const SELECTOR_NAME = 'name';
const SELECTOR_VALIDATION_DATE_COMPARE_FIELD = 'data-validation-date-compare-field';
const SELECTOR_VALIDATION_DATE_MESSAGE = 'data-validation-date-message';
const SELECTOR_VALIDATION_DATE_BEFORE_MESSAGE = 'data-validation-date-before-message';
const SELECTOR_VALIDATION_DATE_SAME_MESSAGE = 'data-validation-date-same-message';

const SELECTOR_FILTER_SCROLL = '.js-lta-filter-panel-scroll';
// @debt There is no html element with this class name, is this for backwards compatibility?
const SELECTOR_FILTER_SCROLL_CONTENT = '.js-lta-filter-panel-scroll-content';
const SELECTOR_SORT_FILTER = '.js-sort-by-filter';

const NAME = 'FilterPanel';

// This is 550 because the DatePicker has a magic interval of 500 and this should be called after that.
const PICKERS_DELAY = 550;
const CLICK = 'click'

class FilterPanel extends BaseComponent {
	constructor(element) {
		super(element);

		this._filterPanelWrapper = element;
		if (this._filterPanelWrapper) {
			this.initFilter();
		}
	}

	static get NAME() {
		return NAME;
	}

	get hasDatePickers() {
		return this.duetDatepickerSelectors.length > 0;
	}

	getQueryParams() {
		return window?.location?.search?.split('?')?.[1]?.split('&')?.map(item => {
			const [key, value] = item.split('=');
			return { key,  value }
		})
	}

	checkTagContainerVisibility() {
		const tagGroupElement = SelectorEngine.findOne('.lta-filter-tag-group', this._filterPanelWrapper);
		if (tagGroupElement) {
			const tagContainerWithClose = SelectorEngine.findOne('.lta-filter-tags-container', tagGroupElement);
			const tagElement = SelectorEngine.find('.lta-tag', tagContainerWithClose);

			if (tagElement?.length > 0) {
				tagGroupElement?.classList.add('u-flex');
				tagGroupElement?.classList.remove('u-hidden');
			} else {
				tagGroupElement?.classList.remove('u-flex');
				tagGroupElement?.classList.add('u-hidden');
			}
		}
	}

	createTagElement(key, value) {
		return `<div id="${key}" class="lta-tag lta-tag--close u-pb-1 u-pt-1 lta-tag__wrapper" data-value="${value}">
					<span>${value}</span>
					<svg class="fe-icon lta-tag__close-icon" width="1.5em" height="1.5em" fill="currentColor" style="margin-left: 0.2rem; vertical-align: middle; cursor: pointer;">
						<use xlink:href="/static/images/icons/lta-icons.svg#close"></use>
					</svg>
				</div>`;
	}

	initialFilterTags() {
		this.tagContainer = SelectorEngine.findOne('.lta-filter-tags-container', this._filterPanelWrapper);
		if (this.tagContainer) {
			this.tagButtons = SelectorEngine.find('.lta-tag__close-icon', this.tagContainer);
			for (const tagButton of this.tagButtons) {
				EventHandler.on(tagButton, CLICK, (event) => {
					this.removeQueryParam(event?.currentTarget?.parentElement);
				});
			}
		}
	}

	removeQueryParam(event) {
		const dataKey = Manipulator.getDataAttribute(event, 'urlKey');
		const dataValue = Manipulator.getDataAttribute(event, 'urlValue');
		const currentURL = new URL(window.location.href);
		const params = new URLSearchParams(currentURL?.search);
		if(dataKey == "timeFrom" || dataKey == "timeTo") {
			if(params.has("timeFrom")) {
				params.delete("timeFrom")
			}
			if(params.has("timeTo")) {
				params.delete("timeTo")
			}
		}else {
			params.delete(dataKey, dataValue);
		}
		currentURL.search = params.toString();
		let modifiedUrl = currentURL.toString();
		window.location.href = modifiedUrl;
	}

	async initFilter() {
		this.inputs = SelectorEngine.find('input', this._filterPanelWrapper);
		this.nestedInputs = SelectorEngine.find('input.nested', this._filterPanelWrapper);
		this.selects = SelectorEngine.find('select', this._filterPanelWrapper);
		this.duetDatepickerSelectors = SelectorEngine.find('duet-date-picker', this._filterPanelWrapper);
		this.isModalFilter = this._filterPanelWrapper.getAttribute('data-filter-modal');
		this.initialFormValues = [];
		this.currentFormValues = [];

		// this.queryParams = this.getQueryParams();
		this.initialFilterTags();
		// this.checkTagContainerVisibility();

		// implement scroll logic
		if (this._filterPanelWrapper.getAttribute('data-filter-scroll')) {
			this.handleScroll();
			window.addEventListener(
				'scroll',
				throttle(() => {
					this.handleScroll();
				}, THROTTLE_INTERVAL_MS)
			);
		}

		// wait for duet date picker to be defined before attaching listeners
		if (this.hasDatePickers) {
			const promises = [...this.duetDatepickerSelectors].map((selector) =>
				window.customElements.whenDefined(selector.localName)
			);

			await Promise.all(promises)
				.then(() =>
					// TODO - As we have a promise, we shouldn't need timeout
					setTimeout(() => {
						this.attachEventListeners();
						this.datePickerValidation();
						this.setInitialFormValues();
					}, PICKERS_DELAY)
				)
				.catch((error) => {
					// eslint-disable-next-line no-console
					console.error('Filter Panel - on when defined duet-date-picker:', error);
				});
		} else {
			this.attachEventListeners();
			this.datePickerValidation();
			this.setInitialFormValues();
		}
	}

	datePickerValidation() {
		// build validator fields from duet datepicker input name
		const fields = {};
		for (const duetElement of this.duetDatepickerSelectors) {
			const fieldName = duetElement.getAttribute(SELECTOR_NAME);
			const compareDateField = duetElement.getAttribute(SELECTOR_VALIDATION_DATE_COMPARE_FIELD);
			const errorDateMessage = duetElement.getAttribute(SELECTOR_VALIDATION_DATE_MESSAGE);
			const errorDateMessageBefore = duetElement.getAttribute(SELECTOR_VALIDATION_DATE_BEFORE_MESSAGE);
			const errorDateMessageSame = duetElement.getAttribute(SELECTOR_VALIDATION_DATE_SAME_MESSAGE);

			fields[fieldName] = {
				validators: {
					callback: {
						message: errorDateMessage,
						callback: (validation) => {
							const input = validation.element.children[0].children[0].children[1].value;

							if (input === '') return true;

							// checks if date is valid
							if (errorDateMessage && !(dayjs(input).format(DATE_FORMAT_YYYY_MM_DD) === input && input.length === 10))
								return false;
							const fieldToCompare = SelectorEngine.findOne(`[name="${compareDateField}"]`, this._filterPanelWrapper)
								.element.children[0].children[0].children[1].value;

							// checks if dates are the same
							if (errorDateMessageSame && input === fieldToCompare)
								return {
									valid: false,
									message: errorDateMessageSame
								};

							// used to check if one date is before another
							if (compareDateField && dayjs(input).isBefore(dayjs(fieldToCompare))) {
								return {
									valid: false,
									message: errorDateMessageBefore
								};
							}

							return true;
						}
					}
				}
			};
		}

		this.fv = formValidation(this._filterPanelWrapper, {
			fields,
			plugins: {
				trigger: new Trigger(),
				submitButton: new SubmitButton(),
				defaultSubmit: new DefaultSubmit(),
				message: new Message({
					clazz: 'lta-text-input__label-instruction',
					container: function (_field, element) {
						let fieldType = element.getAttribute('type');
						if (!fieldType) {
							fieldType = `${element.nodeName.toLowerCase()}`;
						}
						const inputWrapper = element.closest(ValidationUtils.getFieldWrapper(fieldType));
						return SelectorEngine.findOne('.lta-text-input--error', inputWrapper);
					}
				})
			}
		});
	}

	setInitialFormValues() {
		this.checkInputs(true);
		this.checkSelects(true);
		this.checkDuetDatePicker(true);
		this.currentFormValues = this.initialFormValues;
	}

	revalidateForm() {
		// we need a delay or validation doesn't fire for datepicker
		setTimeout(() => {
			for (const validation of this.duetDatepickerSelectors) {
				this.fv.revalidateField(validation.name);
			}
		}, 100);
	}

	attachEventListeners() {
		// modal trigger
		if (this.isModalFilter) {
			const modalFilter = new Modal(SelectorEngine.findOne('#filter-panel-modal'), {});
			const modalTriggerElement = SelectorEngine.find('.js-filter-panel-modal-trigger');
			for (const e of modalTriggerElement) e.addEventListener('click', () => modalFilter.show());
		}

		for (const input of this.inputs) {
			input.addEventListener('input', (_event) => {
				this.checkFormValues();
			});
		}

		for (const select of this.selects) {
			select.addEventListener('change', (_event) => {
				if (select?.id === 'js-lta-select-sort') {
					this._element?.submit();
				} else {
					this.checkFormValues();
				}
			});
		}

		// handle sort by select
		if (this._filterPanelWrapper.getAttribute('data-filter-sort')) {
			const sort = SelectorEngine.findOne('.js-sort-by-filter', this._filterPanelWrapper);
			if (sort)
				sort.addEventListener('change', (_event) => {
					// disable validation when sort by used
					for (const duetElementName of this.duetDatepickerSelectors) {
						const name = duetElementName.element.children[0].children[0].children[1].name;
						this.fv.disableValidator(name);
					}
					this.fv.validate();
				});
		}

		if (!this.hasDatePickers) return;

		for (const dateDuetElement of this.duetDatepickerSelectors) {
			const input = dateDuetElement.children[0].children[0].children[0];
			const hiddenInput = dateDuetElement.children[0].children[0].children[1];
			// event for when user manually types into duet datepicker input
			input.addEventListener('input', (_event) => {
				const value = _event.target.value;

				// reverse date to YYYY-MM-DD so it can be compatible with DuetDatePicker
				if (value.length === 10) {
					const reverseDate = value.split('-');
					hiddenInput.value = `${reverseDate[2]}-${reverseDate[1]}-${reverseDate[0]}`;
				} else {
					hiddenInput.value = value;
				}

				this.checkFormValues();
				this.revalidateForm();
			});

			if (this.isModalFilter) continue;

			// fixes scroll bar issue when datepicker last or second to last
			if (dateDuetElement.closest('.lta-accordion--item'))
				dateDuetElement.closest('.lta-accordion--item').classList.add('lta-filter-panel__date-item');

			// dynamic adjust height, so datepicker doesn't have a scroll bar
			dateDuetElement.addEventListener('duetOpen', (_event) => {
				const target = _event.target;
				target.parentElement.style.height = `${target.element.children[0].children[1].offsetHeight + 100}px`;
			});

			dateDuetElement.addEventListener('duetClose', (_event) => {
				const target = _event.target;
				target.parentElement.style.height = `auto`;

				this.checkFormValues();
				this.revalidateForm();
			});
		}
	}

	checkFormValues() {
		this.currentFormValues = [];
		this.checkInputs();
		this.checkSelects();
		this.checkDuetDatePicker();
		this.hasButton();
	}

	checkInputs(isInitialFormValues) {
		for (const input of this.inputs) {
			const { type, value, checked } = input;
			const inputValue = type === 'checkbox' || type === 'radio' ? checked : value;
			if (input.classList.contains('nested')) {
				if (inputValue) {
					input?.nextElementSibling?.nextElementSibling?.classList.remove('u-hidden');
				} else {
					input?.nextElementSibling?.nextElementSibling?.classList.add('u-hidden');
					this.childInputs = SelectorEngine.find('input', input?.nextElementSibling?.nextElementSibling);
					for (const input of this.childInputs) {
						input.checked = false;
					}
				}
			} else {
				isInitialFormValues ? this.initialFormValues.push(inputValue) : this.currentFormValues.push(inputValue);
			}
		}
	}

	checkSelects(isInitialFormValues) {
		for (const select of this.selects) {
			const isFormIgnore = Manipulator.getDataAttribute(select, 'formIgnore');
			if (!isFormIgnore) {
				isInitialFormValues ? this.initialFormValues.push(select.value) : this.currentFormValues.push(select.value);
			}
		}
	}

	checkDuetDatePicker(isInitialFormValues) {
		if (!this.hasDatePickers) return;

		for (const dateDuetElement of this.duetDatepickerSelectors) {
			const hiddenInput = dateDuetElement.children[0].children[0].children[1].value;

			if (isInitialFormValues) {
				this.initialFormValues.push(dateDuetElement.value, hiddenInput);
			} else {
				this.currentFormValues.push(dateDuetElement.value, hiddenInput);
			}
		}
	}

	isFormSame(initial, current) {
		return initial.length === current.length && initial.every((value, index) => value === current[index]);
	}

	hasButton() {
		const formButtons = SelectorEngine.findOne('.js-lta-form-buttons', this._filterPanelWrapper);
		const isSame = this.isFormSame(this.initialFormValues, this.currentFormValues);
		!isSame ? formButtons.classList.remove('u-hidden') : formButtons.classList.add('u-hidden');
		if (!this.isModalFilter) return;

		!isSame
			? this._filterPanelWrapper.classList.add('is-active-btn')
			: this._filterPanelWrapper.classList.remove('is-active-btn');
	}

	showPosition(element) {
		element.style.position = 'static';
		element.getBoundingClientRect().top;
	}

	handleScroll() {
		const windowHeight = window.innerHeight;
		const scrollElement = SelectorEngine.findOne(SELECTOR_FILTER_SCROLL, this._filterPanelWrapper);
		const scrollContent = SelectorEngine.findOne(SELECTOR_FILTER_SCROLL_CONTENT, this._filterPanelWrapper);
		const sortByElement = SelectorEngine.findOne(SELECTOR_SORT_FILTER, this._filterPanelWrapper);

		let sortHeight = 0;

		if (sortByElement) sortHeight = sortByElement.getBoundingClientRect().bottom;

		if (windowHeight - scrollContent?.getBoundingClientRect()?.bottom > 0) {
			const space = windowHeight - scrollContent.getBoundingClientRect().bottom + sortHeight;
			const height = windowHeight - space;
			scrollElement.style.maxHeight =
				height >= MAX_SCROLL_HEIGHT ? `${height - BOTTOM_BORDER_HEIGHT}px` : `${MAX_SCROLL_HEIGHT}px`;
		} else {
			scrollElement.style.maxHeight = `${windowHeight - scrollElement.getBoundingClientRect().top}px`;
		}
	}
}

export default FilterPanel;